import {
  Button as MuiButton,
  styled,
  Select as MuiSelect,
  CircularProgress,
  FormControl,
  Box,
  MenuItem as MuiMenuItem,
  TextField,
  Autocomplete,
} from "@mui/material";
import { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "notistack";

import { Modal } from "components/Modal";
import { TextInput } from "components/TextInput";
import { Typography } from "components/Typography";
import { formatRequestPayload } from "utils/helpers";
import { useGetBankQuery, useGetAccountNameMutation } from "api/bank";
import { useCreateBlacklistMutation } from "api/blacklist";

const FLAG_TYPE = {
  ACCOUNT: 1,
  BANK: 2,
};

const Button = styled(MuiButton)(() => ({
  textTransform: "capitalize",
  marginRight: 2,
  boxShadow: "none",
  fontWeight: "400",
  "&:hover": { backgroundColor: "#2D75B6", boxShadow: "none" },
}));

const StyledSelect = styled(MuiSelect)(({ theme, error }) => ({
  ".MuiOutlinedInput-notchedOutline": {
    border: `1px solid ${
      error ? theme.palette.error.main : "#EFF1F4"
    } !important`,
  },
}));

const MenuItem = styled(MuiMenuItem)(() => ({
  fontSize: "12px",
}));

export const FlagAccount = () => {
  const [open, setOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const { mutate: createBlacklist, isPending } = useCreateBlacklistMutation();
  const { data: banksList = [] } = useGetBankQuery();

  const formik = useFormik({
    initialValues: {
      type: FLAG_TYPE.ACCOUNT,
      code: "",
      name: "",
      meta: {
        account: {
          bankName: "",
          bankCode: "",
        },
      },
    },
    validationSchema: Yup.object({
      type: Yup.number().required(),
      name: Yup.string().required(),
      code: Yup.string().when("type", {
        is: (type) => parseInt(type) === FLAG_TYPE.ACCOUNT,
        then: (schema) =>
          schema
            .required("Account number is required")
            .matches(/^\d{10}$/, "Account number must be 10 digits"),
        otherwise: (schema) =>
          schema.string().required("Bank code is required"),
      }),
      meta: Yup.object({
        account: Yup.object({
          bankName: Yup.string().required(),
          bankCode: Yup.string().required("Bank code is required"),
        }),
      }),
    }),
    onSubmit: (values) => {
      createBlacklist(formatRequestPayload(values), {
        onSuccess: (res) => {
          enqueueSnackbar(res.message, { variant: "success" });
          closeAccount();
        },
        onError: (error) => {
          enqueueSnackbar(error.message, { variant: "error" });
        },
      });
    },
  });

  const { mutate: fetchAccountName } = useGetAccountNameMutation({
    onSuccess: (response) => {
      if (
        response?.status === "success" &&
        response.data?.acccount?.accountName
      ) {
        formik.setFieldValue("name", response.data.acccount.accountName);
      } else {
        enqueueSnackbar(response.message || "Could not get account name", {
          variant: "error",
        });
        formik.setFieldValue("accountName", "");
      }
    },
    onError: () => {
      enqueueSnackbar("Could not retrieve account name", { variant: "error" });
      formik.setFieldValue("accountName", "");
    },
  });

  const handleAccountNumberChange = (e) => {
    const accountNumber = e.target.value;
    formik.setFieldValue("code", accountNumber);

    if (accountNumber.length === 10 && formik.values.bankCode) {
      fetchAccountName({
        data: { accountNumber, bankCode: formik.values.meta.account.bankCode },
      });
    }
  };

  const openAccount = () => setOpen(true);
  const closeAccount = () => {
    setOpen(false);
    formik.resetForm();
  };

  return (
    <>
      <Button disableRipple variant="contained" onClick={openAccount}>
        Flag Account
      </Button>
      <Modal open={open} handleClose={closeAccount}>
        <Typography
          variant="emailHeading"
          align="center"
          component="h2"
          sx={{ fontSize: "20px", mb: "15px" }}
        >
          Flag Account
        </Typography>
        <form onSubmit={formik.handleSubmit}>
          <Box display="flex" flexDirection="column" gap="10px">
            <FormControl fullWidth>
              <Typography variant="loginLabel">Flag Type</Typography>
              <StyledSelect
                name="type"
                value={formik.values.type}
                onChange={(e) => {
                  formik.resetForm();
                  formik.handleChange(e);
                }}
                disabled
                size="small"
                error={
                  Boolean(formik.touched.type) && Boolean(formik.errors.type)
                }
              >
                {Object.entries(FLAG_TYPE).map(([label, value]) => (
                  <MenuItem value={value}>
                    {label.substring(0, 1).toUpperCase() +
                      label.substring(1).toLowerCase()}
                  </MenuItem>
                ))}
              </StyledSelect>
            </FormControl>
            <FormControl fullWidth>
              <Typography variant="loginLabel">Choose Bank</Typography>
              <Autocomplete
                options={banksList || []}
                getOptionLabel={(option) => option.name || ""}
                value={banksList?.find(
                  (i) => i.bankCode === formik.values.bankCode
                )}
                onChange={(e, value) => {
                  formik.setFieldValue("meta", {
                    account: {
                      bankName: value?.name || "",
                      bankCode: value?.bankCode || "",
                    },
                  });
                  if (parseInt(formik.values.type) === FLAG_TYPE.BANK) {
                    formik.setFieldValue("code", value?.bankCode || "");
                    formik.setFieldValue("name", value?.name || "");
                  } else if (
                    parseInt(formik.values.type) === FLAG_TYPE.ACCOUNT
                  ) {
                    formik.setFieldValue("code", "");
                    formik.setFieldValue("name", "");
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    error={
                      formik.touched.meta?.account?.bankCode &&
                      Boolean(formik.errors?.meta?.account?.bankCode)
                    }
                  />
                )}
              />
            </FormControl>
            {parseInt(formik.values.type) === 1 && (
              <>
                <TextInput
                  label="Account number"
                  type="text"
                  name="code"
                  value={formik.values.code}
                  onChange={handleAccountNumberChange}
                  onKeyPress={(e) => {
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  inputProps={{ pattern: "[0-9]*", maxLength: 10 }}
                  error={
                    Boolean(formik.touched.code) && Boolean(formik.errors.code)
                  }
                  disabled={!formik.values.meta.account.bankCode}
                />
                <TextInput
                  label="Account name"
                  type="text"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  disabled
                  error={
                    Boolean(formik.touched.name) && Boolean(formik.errors.name)
                  }
                />
              </>
            )}
          </Box>
          <Button
            type="submit"
            variant="contained"
            disabled={isPending}
            fullWidth
            sx={{ padding: "10px 0px", mt: "30px" }}
          >
            Flag Account
            {isPending && <CircularProgress size={18} sx={{ ml: 0.5 }} />}
          </Button>
        </form>
      </Modal>
    </>
  );
};
